import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Upload, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { FloatingParticles } from '../../components/animations/FloatingParticles';
import CVConfirmationModal from '../../components/modals/CVConfirmationModal';
import apiClient from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Extract jobId from location state (passed from JobDetailPage)
  const jobId = location.state?.jobId;
  
  const [step, setStep] = useState<'upload' | 'processing' | 'success'>('upload');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [wasUpdate, setWasUpdate] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fullExtractedInfo, setFullExtractedInfo] = useState<any>(null); // Store full extracted data

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF, DOC, or DOCX file.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB.');
      return;
    }

    // Save file to state for later use (in case user needs to update profile)
    setUploadedFile(file);

    setError(null);
    setIsLoading(true);
    setStep('processing');

    try {
      const formData = new FormData();
      formData.append('cv_file', file);
      formData.append('confirm_update', 'false'); // Don't auto-update - show modal if user exists

      const response = await apiClient.post('/auth/register-with-cv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update auth state with token and user data (this makes isAuthenticated = true)
      if (response.data?.data?.token && response.data?.data?.user) {
        loginWithToken(response.data.data.token, response.data.data.user);
      }

      // Success
      setStep('success');
      setWasUpdate(false);

      // Auto redirect after 3 seconds
      setTimeout(() => {
        // If user came from job application, navigate back to job page with checkStatusOnly flag
        // This will trigger the interview modal (same behavior as login)
        if (jobId) {
          navigate(`/careers/${jobId}`, {
            state: { checkStatusOnly: true },
            replace: true
          });
        } else {
          navigate('/dashboard');
        }
      }, 3000);
    } catch (err: any) {
      if (err.response?.status === 409) {
        // User already exists - save BOTH display data and full extracted info
        setUserExists(true);
        setExtractedData(err.response.data.extractedData);
        setFullExtractedInfo(err.response.data.fullExtractedInfo); // Save full data for re-use
        setShowUpdateModal(true);
        setStep('upload'); // Go back to upload step
        setIsLoading(false);
        return;
      }
      // Handle various error formats
      let errorMessage = 'An error occurred during registration';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.detail) {
        // Handle FastAPI validation errors
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          // Extract first error message from validation errors
          errorMessage = err.response.data.detail[0]?.msg || 'Validation error occurred';
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setStep('upload');
      setIsLoading(false);
    }
  };

  const handleUpdateConfirm = async () => {
    // Handle update existing user profile using ALREADY EXTRACTED data (no re-analysis!)
    setShowUpdateModal(false);
    setStep('processing');
    setIsLoading(true);

    try {
      // Use the file saved in state + confirm_update=true to trigger update
      if (!uploadedFile) {
        setError('No file found. Please upload your CV again.');
        setStep('upload');
        setIsLoading(false);
        return;
      }

      // Send the file + pre-extracted data to avoid duplicate Gemini call!
      const formData = new FormData();
      formData.append('cv_file', uploadedFile);
      formData.append('confirm_update', 'true');

      // 🚀 Send pre-extracted data to skip expensive AI re-extraction
      if (fullExtractedInfo) {
        formData.append('pre_extracted_cv_data', JSON.stringify(fullExtractedInfo));
      }

      const response = await apiClient.post('/auth/register-with-cv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update auth state with token and user data (this makes isAuthenticated = true)
      if (response.data?.data?.token && response.data?.data?.user) {
        loginWithToken(response.data.data.token, response.data.data.user);
      }

      setStep('success');
      setWasUpdate(true);

      setTimeout(() => {
        // If user came from job application, navigate back to job page with checkStatusOnly flag
        // This will trigger the interview modal (same behavior as login)
        if (jobId) {
          navigate(`/careers/${jobId}`, {
            state: { checkStatusOnly: true },
            replace: true
          });
        } else {
          navigate('/dashboard');
        }
      }, 3000);
    } catch (err: any) {
      // Handle various error formats
      let errorMessage = 'An error occurred during update';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.detail) {
        // Handle FastAPI validation errors
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          // Extract first error message from validation errors
          errorMessage = err.response.data.detail[0]?.msg || 'Validation error occurred';
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setStep('upload');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCancel = () => {
    setShowUpdateModal(false);
    setStep('upload');
  };

  // Handle "Just Login" option from modal
  const handleJustLogin = () => {
    setShowUpdateModal(false);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <FloatingParticles />
      
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/5 via-transparent to-yellow-500/5 pointer-events-none" />
      
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="max-w-md w-full"
        >
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl sm:rounded-3xl p-8 sm:p-10 shadow-2xl hover:shadow-amber-500/10 hover:bg-white/[0.07] hover:border-white/15 transition-all duration-300">
            
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="w-20 h-20 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-black/80 ring-1 ring-white/10"
              >
                {step === 'upload' && <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-10 h-10" />}
                {step === 'processing' && <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-10 h-10" />}
                {step === 'success' && <CheckCircle className="w-10 h-10 text-green-300" />}
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl sm:text-4xl font-bold text-gradient-primary mb-3 leading-tight"
              >
                {step === 'upload' && 'Start Your Journey'}
                {step === 'processing' && 'Processing Your CV'}
                {step === 'success' && (wasUpdate ? 'Profile Updated!' : 'Welcome Aboard!')}
              </motion.h2>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-gray-300 text-lg"
              >
                {step === 'upload' && 'Upload your CV and we\'ll handle the rest'}
                {step === 'processing' && 'Extracting your professional information...'}
                {step === 'success' && (wasUpdate 
                  ? 'Your profile has been successfully updated with the latest CV information.' 
                  : 'Your account has been created and profile populated from your CV.')}
              </motion.p>
            </div>

            {/* Upload Section */}
            {step === 'upload' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-6"
              >
                <div className="border-2 border-dashed border-white/20 hover:border-amber-400/40 rounded-2xl p-8 text-center transition-all duration-300 group bg-white/[0.02] hover:bg-white/[0.05]">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="w-16 h-16 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-amber-400/20"
                  >
                    <Upload className="w-8 h-8 text-amber-300 group-hover:scale-110 transition-transform duration-300" />
                  </motion.div>
                  
                  <h3 className="text-xl font-semibold text-white mb-3">Upload Your CV</h3>
                  <p className="text-gray-300 mb-6">Drag and drop your CV here or click to browse</p>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    disabled={isLoading}
                  />
                  
                  <motion.button
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-amber-500/25"
                  >
                    {isLoading ? 'Uploading...' : 'Choose File'}
                  </motion.button>
                  
                  <p className="text-sm text-gray-400 mt-4">
                    Supported formats: PDF, DOC, DOCX (Max 10MB)
                  </p>
                </div>
                
                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-red-900/20 border border-red-500/30 text-red-300 p-4 rounded-xl backdrop-blur-xl"
                  >
                    <div className="flex items-center gap-3">
                      <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-5 h-5" />
                      <p>{error}</p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Processing Section */}
            {step === 'processing' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <div className="relative mb-8">
                  <div className="w-32 h-32 border-4 border-amber-500/20 border-t-amber-400 rounded-full animate-spin mx-auto"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    >
                      <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-12 h-12" />
                    </motion.div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden backdrop-blur-xl">
                    <motion.div
                      className="h-full bg-gradient-to-r from-amber-500 to-yellow-500"
                      initial={{ width: '0%' }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 3, ease: 'easeInOut' }}
                    />
                  </div>
                  <p className="text-gray-300 text-lg">Analyzing your CV and extracting key information...</p>
                </div>
              </motion.div>
            )}

            {/* Success Section */}
            {step === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl p-8 mb-8 border border-green-400/20 backdrop-blur-xl"
                >
                  <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
                  <h3 className="text-2xl font-semibold text-white mb-4">
                    {wasUpdate ? 'Profile Updated!' : 'Account Created Successfully!'}
                  </h3>
                  <p className="text-gray-300 text-lg leading-relaxed mb-4">
                    {wasUpdate
                      ? 'Your profile has been updated with the latest CV information.'
                      : 'Your account has been created and profile populated from your CV.'}
                  </p>
                  {jobId && (
                    <p className="text-amber-300 text-base leading-relaxed">
                      Redirecting to your interview...
                    </p>
                  )}
                </motion.div>

                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (jobId) {
                      navigate(`/careers/${jobId}`, {
                        state: { checkStatusOnly: true },
                        replace: true
                      });
                    } else {
                      navigate('/dashboard');
                    }
                  }}
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-amber-500/25"
                >
                  <span>Continue</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </motion.div>
            )}
          </div>
          
          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-center mt-8"
          >
            <p className="text-gray-300 text-lg">
              Already have an account?{' '}
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => navigate('/login')}
                className="text-amber-400 hover:text-amber-300 font-semibold transition-colors"
              >
                Sign in here
              </motion.button>
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* CV Confirmation Modal */}
      <CVConfirmationModal
        isOpen={showUpdateModal}
        onClose={handleUpdateCancel}
        onUpdateProfile={handleUpdateConfirm}
        onJustLogin={handleJustLogin}
        userExists={userExists}
        extractedData={extractedData || { name: '', email: '', phone: '' }}
      />
    </div>
  );
}